import { useSearchParams } from "react-router-dom";
import { Leaf } from "lucide-react";

// Theme-derived tints so the brand surface tracks the app's green tokens
// instead of a separate hardcoded emerald palette.
const BRAND_ACCENT = "text-[var(--primary-45-white)]";
const BRAND_DOT = "bg-[var(--primary-45-white)]";
const BRAND_GLOW = "shadow-[0_0_12px_var(--primary-8)]";

const REF_LOGO_MAP: Record<string, string> = {
  ccc: "/assets/images/ccc-logo-dark.png",
  cii: "/assets/images/cii-logo.png",
};

export function BrandPanel() {
  const [searchParams] = useSearchParams();
  const refKey = searchParams.get("ref") ?? "";
  const refLogo = REF_LOGO_MAP[refKey];
  return (
    <div
      className="relative hidden lg:flex lg:w-[55%] overflow-hidden bg-[var(--primary-10-black)]"
      style={{
        backgroundImage: [
          "radial-gradient(ellipse 72% 58% at 0% 0%, var(--primary-30), transparent 70%)",
          "radial-gradient(ellipse 62% 72% at 100% 100%, var(--accent-40), transparent 72%)",
          "linear-gradient(135deg, var(--primary-32-black) 0%, var(--primary-22-black) 48%, var(--primary-10-black) 100%)",
        ].join(", "),
      }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full bg-primary/20 animate-blob blur-3xl" />
        <div className="absolute top-1/3 -right-20 h-[400px] w-[400px] rounded-full bg-primary/20 animate-blob-delay-1 blur-3xl" />
        <div className="absolute -bottom-20 left-1/3 h-[350px] w-[350px] rounded-full bg-accent/20 animate-blob-delay-2 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-primary/10 animate-blob-float blur-3xl" />
      </div>
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle, currentColor 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
        <div className="animate-fade-in-up">
          <img
            src="/planetwise-logo.png"
            alt="Planetwise"
            className="h-10 mb-2"
          />
        </div>

        {/* Conditional partner logo (e.g. CCC, CII) via ?ref= query param */}
        {refLogo && (
          <img
            src={refLogo}
            alt="Partner logo"
            className="absolute top-10 right-10 h-24 object-contain"
          />
        )}
        <div className="flex-1 flex flex-col justify-center max-w-lg">
          <div className="space-y-8">
            <div className="animate-fade-in-up stagger-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white/90 backdrop-blur-sm border border-white/10 mb-6">
                <Leaf size={14} className={BRAND_ACCENT} />
                <span>ESG Intelligence Platform</span>
              </div>
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-[1.15] tracking-tight animate-fade-in-up stagger-2">
              See the full picture. <br />
              <span className={BRAND_ACCENT}>Make your next move count.</span>
            </h1>
            <p className="text-lg text-white/75 leading-relaxed max-w-md animate-fade-in-up stagger-3">
              Track emissions across Scope 1-3, set science-based targets, and
              drive your organisation toward net zero — all in one place.
            </p>
            <div className="flex flex-wrap gap-3 pt-1 animate-fade-in-up stagger-4">
              <span
                className={`animate-float inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] px-3.5 py-1.5 text-xs font-medium text-white/85 backdrop-blur-sm border border-white/[0.08] ${BRAND_GLOW}`}
                style={{ animationDuration: "3.5s", animationDelay: "0s" }}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${BRAND_DOT} animate-pulse-glow`}
                />
                GHG Protocol
              </span>
              <span
                className={`animate-float inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] px-3.5 py-1.5 text-xs font-medium text-white/85 backdrop-blur-sm border border-white/[0.08] ${BRAND_GLOW}`}
                style={{ animationDuration: "4s", animationDelay: "0.5s" }}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${BRAND_DOT} animate-pulse-glow`}
                  style={{ animationDelay: "0.3s" }}
                />
                ISO 14064
              </span>
              <span
                className={`animate-float inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] px-3.5 py-1.5 text-xs font-medium text-white/85 backdrop-blur-sm border border-white/[0.08] ${BRAND_GLOW}`}
                style={{ animationDuration: "3.8s", animationDelay: "1s" }}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${BRAND_DOT} animate-pulse-glow`}
                  style={{ animationDelay: "0.6s" }}
                />
                SBTi Aligned
              </span>
              <span
                className={`animate-float inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] px-3.5 py-1.5 text-xs font-medium text-white/85 backdrop-blur-sm border border-white/[0.08] ${BRAND_GLOW}`}
                style={{ animationDuration: "4.2s", animationDelay: "1.5s" }}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${BRAND_DOT} animate-pulse-glow`}
                  style={{ animationDelay: "0.9s" }}
                />
                Scope 1–3
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
